import { readPref, writePref } from './prefs';
import type { BattleView } from './types';

/**
 * Option joueur « Lancer automatique » — COMMENTS v0.4.1 §2 :
 * « Une option pour lancer automatiquement les dés doit être disponible durant
 * les accalmies. Cette option est permanente jusqu'à désactivation. »
 *
 * Elle est donc **persistée dans le navigateur** (comme le son et le tri de
 * l'inventaire, pas dans la sauvegarde : c'est un confort d'interface, pas de
 * la progression) et appliquée à la vue de combat au début de chaque combat.
 * Indépendante du mode debug (DECISIONS §E) : la case du panneau de debug lit
 * et écrit la même préférence.
 */

export const AUTO_THROW_KEY = 'diceordie.autoThrow';

/** Désactivé par défaut (DECISIONS §E). */
export function readAutoThrow(): boolean {
  return readPref(AUTO_THROW_KEY) === '1';
}

export function writeAutoThrow(on: boolean): void {
  writePref(AUTO_THROW_KEY, on ? '1' : '0');
}

/** La vue de combat sait-elle lancer toute seule (`battle.setAutoThrow`) ? */
export function supportsAutoThrow(battle: BattleView): boolean {
  return typeof battle.setAutoThrow === 'function';
}

/**
 * Applique la préférence à la vue de combat. Sans `setAutoThrow` (vue de
 * secours ou livraison en cours), l'option reste mémorisée et sera appliquée
 * dès que la vue l'implémentera.
 */
export function applyAutoThrow(battle: BattleView, on: boolean = readAutoThrow()): boolean {
  if (typeof battle.setAutoThrow !== 'function') return false;
  try {
    battle.setAutoThrow(on);
    return true;
  } catch {
    return false;
  }
}

/** Événement interne : deux interfaces règlent la même option (Accalmie et debug). */
const CHANGE_EVENT = 'dod:autothrow';

/** Écrit la préférence, l'applique, et prévient les autres écrans ouverts. */
export function setAutoThrow(battle: BattleView, on: boolean): void {
  writeAutoThrow(on);
  applyAutoThrow(battle, on);
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: on }));
  } catch {
    /* CustomEvent indisponible : les deux cases se resynchronisent au rendu suivant */
  }
}

/** S'abonne aux changements de l'option (renvoie le désabonnement). */
export function onAutoThrowChange(fn: (on: boolean) => void): () => void {
  const handler = (): void => fn(readAutoThrow());
  try {
    window.addEventListener(CHANGE_EVENT, handler);
  } catch {
    return () => undefined;
  }
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}
